@import "stylesheets/sdx/utilities/screenreaders";
@import "../../globals/scss/label";

$line-height: 24px;

:host {
  width: 100%;
}

.component {
  &:not(.readonly):not(.disabled):not(.invalid) {
    .wrapper {
      .input:hover:not(:focus) {
        border-color: $color-input-normal-form-hover;

        .badge {
          background-color: $token-surface-interaction-hover;
        }
      }
    }
  }

  &:focus-within {
    &.invalid {
      sdx-validation-message {
        display: none;
      }
    }
  }

  &.readonly,
  &.disabled {
    .wrapper {
      .input {
        color: $color-input-disabled-text;
        border-color: $color-input-disabled-text;
        cursor: not-allowed;

        &::placeholder,
        .placeholder {
          color: $color-input-disabled-text;
        }
      }
    }
  }

  &.invalid {
    .wrapper {
      .input {
        color: $color-input-invalid-text;
        border-color: $color-input-invalid-form;
      }
    }
  }

  &.valid,
  &.date {
    .wrapper {
      .input {
        padding-right: $baseline-6; // space for tick and calendar icon
      }
    }
  }

  &.date {
    .wrapper {
      .input {
        min-width: 152px; // meaningful min-width for the expected default format DD.MM.YYYY, added for sdx-filter
      }
    }
  }

  &:not(.readonly) {
    .wrapper {
      .input:focus {
        @include focus-visible;

        border-color: $token-border-color-interaction-default;

        .badge {
          @include badge(null, $color-int-blue2-dark-theme);

          text-overflow: clip; // otherwise ellipsis are shown from the 2nd digit onwards
        }

        &::placeholder {
          opacity: 0;
        }
      }
    }
  }

  &.search {
    .wrapper {
      .input {
        padding-right: 92px; // space for clear + search button
      }
    }
  }

  .wrapper {
    position: relative; // for .inline-buttons

    .input {
      border: $border-width solid $color-input-normal-form;
      border-radius: $border-radius-medium;
      height: $form-input-height;
      padding: 0 $baseline-2;
      line-height: $line-height;
      letter-spacing: -0.1px;
      font: inherit;
      font-size: 18px;
      position: relative;
      outline: none;
      background-color: $color-input-bg;
      width: 100%;
      color: $color-input-normal-text;
      user-select: text;
      appearance: none;
      display: flex;
      align-items: center;
      caret-color: $token-text-color-interaction-default;

      sdx-text-truncate {
        min-width: 0; // for flexbox calculation (see https://css-tricks.com/flexbox-truncated-text)
        width: auto;
      }

      .badge {
        @include badge;
        margin-left: $baseline-2;
        text-overflow: clip; // otherwise ellipsis are shown from the 2nd digit onwards

        &:empty {
          visibility: hidden;
        }
      }

      &::placeholder,
      .placeholder {
        color: $color-input-placeholder;
        transition: inherit; // not working in FF because transitions on this pseudo element are not defined in the HTML standard
      }

      &::-ms-clear {
        display: none;
      }

      // Firefox: prevent drawing red shadow (last checked on Firefox 73.0)
      &:required,
      &:invalid {
        box-shadow: none;
      }

      &[type="search"] {
        -webkit-appearance: none;

        &::-webkit-search-cancel-button,
        &::-webkit-search-decoration {
          -webkit-appearance: none;
        }
      }

      &[type="number"] {
        -moz-appearance: textfield;

        &::-webkit-inner-spin-button,
        &::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
      }

      &[data-input] {
        &::-webkit-calendar-picker-indicator {
          display: none;
        }
      }

      &.textarea {
        // Textareas padding should look the same as the inputs one
        $textarea-v-padding: $baseline + 3px;

        padding-top: $textarea-v-padding;
        padding-bottom: $textarea-v-padding;
        resize: none;
        line-height: $line-height; // set it explicitly to enable calculation with JavaScript
      }
    }

    .inline-buttons {
      display: flex;
      position: absolute;
      top: 1px; // fine adjustment
      right: 1px; // fine adjustment

      .clear-button sdx-button {
        padding: 11px 14px; // fine adjustment
      }

      .search-button {
        padding: 7px; // fine adjustment
      }

      .loading-spinner {
        padding: 9px; // fine adjustment
      }
    }

    // Icons inside the input field
    .calendar-button {
      position: absolute;
      top: 0;
      right: 0;
      padding: 6px;
      z-index: 1;
    }

    .checkmark-icon {
      position: absolute;
      top: 8px;
      right: 8px;
      color: $color-int-green;
    }

    sdx-menu-flyout {
      position: absolute;
      width: 62px; // fine adjustment to align popup to input field
      height: 100%;
    }
  }

  .label-row {
    display: flex;
    justify-content: space-between;
    gap: $baseline;
  }
}

.component.sdx--dark-theme {
  &:not(.readonly):not(.disabled):not(.invalid) {
    .wrapper {
      .input:hover:not(:focus) {
        border-color: $color-int-gray-dark-theme--active;

        .badge {
          background-color: $color-int-blue2-dark-theme;
        }
      }
    }
  }

  &:not(.readonly) {
    .wrapper {
      .input:focus {
        color: $color-gray-dark-theme;
        border-color: $color-int-blue2-dark-theme;

        .badge {
          background-color: $color-int-blue2-dark-theme;
        }
      }
    }
  }

  &.readonly,
  &.disabled {
    .wrapper {
      .input {
        border-color: $color-disabled-dark-theme;
        &::placeholder,
        .placeholder {
          color: $color-disabled-dark-theme;
        }
      }
    }
  }

  &.invalid {
    .wrapper {
      .input {
        color: $color-invalid-dark-theme;
        border-color: $color-invalid-dark-theme;
      }
    }
  }

  .wrapper {
    .input {
      border-color: $color-int-gray-dark-theme;
      background-color: $color-dusk-tint-2-dark-theme;
      color: $color-gray-dark-theme;
      caret-color: $color-int-blue2-dark-theme;

      &::placeholder,
      .placeholder {
        color: $color-placeholder-dark-theme;
      }

      .badge {
        background-color: $color-int-blue-dark-theme;
      }
    }

    .checkmark-icon {
      color: $color-valid-dark-theme;
    }
  }
}
